import React, { useState, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { Divider } from 'primereact/divider';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { useAuth } from '../contexts/AuthContext';
import { spaService } from '../services/spaService';
import PaymentForm from './checkout/PaymentForm';
import ThreeDSecureModal from './checkout/ThreeDSecureModal';
import { getBrowserFingerprint } from '../utils/browserFingerprint';

const CheckoutDialog = ({ visible, onHide, item, itemType = 'event', onPaymentSuccess }) => {
  const { user } = useAuth();
  const toast = useRef(null);
  
  // Available countries
  const countries = [
    { label: 'United Kingdom', value: 'GB' },
    { label: 'Ireland', value: 'IE' },
    { label: 'France', value: 'FR' },
    { label: 'Germany', value: 'DE' },
    { label: 'Netherlands', value: 'NL' }
  ];
  
  // Payment flow states
  const [paymentStep, setPaymentStep] = useState('details'); // 'details', 'payment', '3dsecure', 'processing'
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [currentOrderId, setCurrentOrderId] = useState(null);
  const [currentTransactionId, setCurrentTransactionId] = useState(null);
  const [error, setError] = useState(null);
  const [customerDetails, setCustomerDetails] = useState({
    firstName: user?.member?.firstName || '',
    lastName: user?.member?.lastName || '',
    email: user?.member?.email || '',
    phone: user?.member?.phone || ''
  });

  const [billingAddress, setBillingAddress] = useState({
    addressLine1: user?.member?.addressDetails?.addressLine1 || '',
    addressLine2: user?.member?.addressDetails?.addressLine2 || '',
    townCity: user?.member?.addressDetails?.townCity || '',
    county: user?.member?.addressDetails?.county || '',
    postCode: user?.member?.addressDetails?.postCode || '',
    country: countries.some(c => c.value === user?.member?.addressDetails?.country) ? user?.member?.addressDetails?.country || '' : ''
  });

  const [specialRequests, setSpecialRequests] = useState('');
  
  // Validation states
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const calculateTotal = () => {
    if (!item) return 0;
    
    if (itemType === 'event') {
      // Find the selected class pricing
      if (item.selectedClassification && item.classes) {
        const selectedClass = item.classes.find(cls => cls.id === item.selectedClassification);
        if (selectedClass) {
          const basePrice = selectedClass.memberPrice || 0;
          const discount = selectedClass.earlyBirdDiscount || 0;
          return Math.max(0, basePrice - discount);
        }
      }
      
      // Fallback to event-level pricing if no class selected
      if (item.price) {
        return item.price;
      }
      const basePrice = item.memberPrice || 0;
      const discount = (item.earlyBirdDiscount || 0);
      return Math.max(0, basePrice - discount);
    }
    
    if (itemType === 'product') {
      // For products, use the price (could be calculated with quantity)
      return item.price || 0;
    }
    
    if (itemType === 'horse-registration') {
      // For horse registration, always £5.00
      return item.price || 5.00;
    }
    
    // Default fallback
    return item.price || 0;
  };

  const getBasePrice = () => {
    if (!item) return 0;
    
    if (itemType === 'event') {
      // Find the selected class pricing
      if (item.selectedClassification && item.classes) {
        const selectedClass = item.classes.find(cls => cls.id === item.selectedClassification);
        if (selectedClass) {
          return selectedClass.memberPrice || 0;
        }
      }
      
      // Fallback to event-level pricing
      return item.memberPrice || 0;
    }
    
    return item.price || 0;
  };

  const getDiscount = () => {
    if (!item) return 0;
    
    if (itemType === 'event') {
      // Find the selected class discount
      if (item.selectedClassification && item.classes) {
        const selectedClass = item.classes.find(cls => cls.id === item.selectedClassification);
        if (selectedClass) {
          return selectedClass.earlyBirdDiscount || 0;
        }
      }
      
      // Fallback to event-level discount
      return item.earlyBirdDiscount || 0;
    }
    
    return 0;
  };

  const getSelectedClassName = () => {
    if (!item || itemType !== 'event') return '';
    
    if (item.selectedClassification && item.classes) {
      const selectedClass = item.classes.find(cls => cls.id === item.selectedClassification);
      return selectedClass ? selectedClass.name : '';
    }
    
    return '';
  };

  // Validation functions
  const validateField = (field, value) => {
    let validation = { isValid: true };

    // Email validation
    if (field === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!value || value.trim() === '') {
        validation = { isValid: false, error: 'Email is required' };
      } else if (!emailRegex.test(value)) {
        validation = { isValid: false, error: 'Please enter a valid email address' };
      }
    }

    // Required text field validation
    if (['firstName', 'lastName', 'addressLine1', 'townCity', 'postCode'].includes(field)) {
      if (!value || value.trim() === '') {
        const fieldNames = {
          firstName: 'First name',
          lastName: 'Last name',
          addressLine1: 'Address line 1',
          townCity: 'City',
          postCode: 'Postcode'
        };
        validation = { isValid: false, error: `${fieldNames[field]} is required` };
      } else if (value.trim().length < 2) {
        validation = { isValid: false, error: 'Must be at least 2 characters' };
      }
    }

    // Country validation
    if (field === 'country') {
      if (!value || value === '') {
        validation = { isValid: false, error: 'Country is required' };
      }
    }

    // Phone validation (optional but if provided must be valid)
    if (field === 'phone' && value && value.trim() !== '') {
      const phoneRegex = /^[\d\s\+\-\(\)]+$/;
      if (!phoneRegex.test(value)) {
        validation = { isValid: false, error: 'Please enter a valid phone number' };
      } else if (value.replace(/\D/g, '').length < 10) {
        validation = { isValid: false, error: 'Phone number must be at least 10 digits' };
      }
    }

    // Postcode validation (basic UK format)
    if (field === 'postCode' && value && value.trim() !== '') {
      const postcodeRegex = /^[A-Z|a-z]{1,2}\d{1,2}[A-Z|a-z]?\s?\d[A-Z|a-z]{2}$/i;
      if (!postcodeRegex.test(value.trim())) {
        validation = { isValid: false, error: 'Please enter a valid UK postcode' };
      }
    }

    setErrors(prev => ({
      ...prev,
      [field]: validation.isValid ? null : validation.error
    }));

    return validation.isValid;
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    
    // Get the current value
    let value;
    if (['firstName', 'lastName', 'email', 'phone'].includes(field)) {
      value = customerDetails[field];
    } else {
      value = billingAddress[field];
    }
    
    validateField(field, value);
  };

  const handleCustomerChange = (field, value) => {
    setCustomerDetails(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (touched[field] && errors[field]) {
      validateField(field, value);
    }
  };

  const handleAddressChange = (field, value) => {
    setBillingAddress(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (touched[field] && errors[field]) {
      validateField(field, value);
    }
  };

  // Validate all required fields
  const validateAllFields = () => {
    const requiredFields = [
      'firstName',
      'lastName', 
      'email',
      'addressLine1',
      'townCity',
      'postCode',
      'country'
    ];

    // Mark all required fields as touched
    const newTouched = {};
    requiredFields.forEach(field => {
      newTouched[field] = true;
    });
    setTouched(newTouched);

    // Validate all fields
    const newErrors = {};
    let isValid = true;

    // Validate customer details
    ['firstName', 'lastName', 'email', 'phone'].forEach(field => {
      const value = customerDetails[field];
      const fieldValidation = validateFieldValue(field, value);
      if (!fieldValidation.isValid) {
        newErrors[field] = fieldValidation.error;
        isValid = false;
      }
    });

    // Validate billing address
    ['addressLine1', 'townCity', 'postCode', 'country'].forEach(field => {
      const value = billingAddress[field];
      const fieldValidation = validateFieldValue(field, value);
      if (!fieldValidation.isValid) {
        newErrors[field] = fieldValidation.error;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  // Helper to validate field value without updating state
  const validateFieldValue = (field, value) => {
    let validation = { isValid: true };

    // Email validation
    if (field === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!value || value.trim() === '') {
        validation = { isValid: false, error: 'Email is required' };
      } else if (!emailRegex.test(value)) {
        validation = { isValid: false, error: 'Please enter a valid email address' };
      }
    }

    // Required text field validation
    if (['firstName', 'lastName', 'addressLine1', 'townCity', 'postCode'].includes(field)) {
      if (!value || value.trim() === '') {
        const fieldNames = {
          firstName: 'First name',
          lastName: 'Last name',
          addressLine1: 'Address line 1',
          townCity: 'City',
          postCode: 'Postcode'
        };
        validation = { isValid: false, error: `${fieldNames[field]} is required` };
      } else if (value.trim().length < 2) {
        validation = { isValid: false, error: 'Must be at least 2 characters' };
      }
    }

    // Country validation
    if (field === 'country') {
      if (!value || value === '') {
        validation = { isValid: false, error: 'Country is required' };
      }
    }

    // Phone validation (optional but if provided must be valid)
    if (field === 'phone' && value && value.trim() !== '') {
      const phoneRegex = /^[\d\s\+\-\(\)]+$/;
      if (!phoneRegex.test(value)) {
        validation = { isValid: false, error: 'Please enter a valid phone number' };
      } else if (value.replace(/\D/g, '').length < 10) {
        validation = { isValid: false, error: 'Phone number must be at least 10 digits' };
      }
    }

    // Postcode validation (basic UK format)
    if (field === 'postCode' && value && value.trim() !== '') {
      const postcodeRegex = /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i;
      if (!postcodeRegex.test(value.trim())) {
        validation = { isValid: false, error: 'Please enter a valid UK postcode' };
      }
    }

    return validation;
  };

  // Step 1: Create order and begin payment
  const handleProceedToPayment = async () => {
    try {
      // Validate all fields first
      if (!validateAllFields()) {
        toast.current?.show({
          severity: 'warn',
          summary: 'Validation Error',
          detail: 'Please correct the errors in the form before proceeding',
          life: 5000
        });
        return;
      }

      setIsProcessing(true);
      setError(null);

      // Create the order first
      const order = await spaService.createOrder({
        currencyCode: 'GBP',
        orderNotes: `Order created by SPA Members App${specialRequests ? ': ' + specialRequests : ''}`,
        customer: {
          email: customerDetails.email,
          firstName: customerDetails.firstName,
          lastName: customerDetails.lastName,
          phoneNumber: customerDetails.phone,
          billingAddress: {
            addressLine1: billingAddress.addressLine1,
            addressLine2: billingAddress.addressLine2,
            city: billingAddress.townCity,
            state: billingAddress.county,
            postalCode: billingAddress.postCode,
            countryCode: billingAddress.country
          }
        }
      });

      if (!order || !order.orderId) {
        throw new Error('Failed to create order');
      }

      // Add order line based on item type
      if (itemType === 'horse-registration') {
        // SE: 26450abd-1dea-43fd-bda5-ff1728b1c5db
        // DEV: f4eaf551-b849-4487-b3e4-e7e3d9c4f464
        await spaService.addOrderLine(order.orderId, "26450abd-1dea-43fd-bda5-ff1728b1c5db", 1, {
          "order-line-is-horse": "True",
          "order-line-horse-name": item?.horseDetails?.horseName || '',
          "order-line-horse-dob": item?.horseDetails?.dateOfBirth || '',
          "order-line-horse-gender": item?.horseDetails?.gender || '',
          "order-line-horse-breed": item?.horseDetails?.breed || '',
          "order-line-horse-colour": item?.horseDetails?.colour || '',
          "order-line-horse-height": item?.horseDetails?.height || '',
          "order-line-horse-socreg": item?.horseDetails?.breedSocietyRegistered ? 'True' : 'False',
          "order-line-horse-socdetails": item?.horseDetails?.breedSocietyDetails || '',
          "order-line-memberid": user?.memberId || ''
        });
      }
      // Add other item types here (events, products, etc.)

      // Begin payment process
      // Store order ID and move to payment step
      setCurrentOrderId(order.orderId);
      setPaymentStep('payment');
      
      toast.current?.show({
        severity: 'success',
        summary: 'Order Created',
        detail: 'Please enter your card details to complete payment',
        life: 3000
      });

    } catch (err) {
      console.error('Error creating order:', err);
      setError(err.message || 'Failed to create order');
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err.message || 'Failed to create order',
        life: 5000
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Step 2: Process card payment
  const handlePaymentSubmit = async (cardDetails) => {
    try {
      setIsProcessing(true);
      setError(null);
      setPaymentStep('processing');

      // Collect browser fingerprint
      const browserFingerprint = getBrowserFingerprint();

      // Prepare payment request matching C# PaymentFormModel
      const paymentRequest = {
        // Card details at top level        
        CardholderName: cardDetails.cardholderName,
        CardType: cardDetails.cardType, // User-selected card type from dropdown
        CardNumber: cardDetails.cardNumber.replace(/\s/g, ''), // Remove spaces
        CardExpiry: `${cardDetails.expiryMonth}/${cardDetails.expiryYear}`, // MM/YY format
        CVV: cardDetails.cvv,
        
        // Billing details
        BillingDetails: {
          FirstName: customerDetails.firstName,
          LastName: customerDetails.lastName,
          Line1: billingAddress.addressLine1,
          Line2: billingAddress.addressLine2 || null,
          Line3: null,
          City: billingAddress.townCity,
          Postcode: billingAddress.postCode,
          Country: 'GB', // Assuming UK only for now,
          DeliveryPhone: customerDetails.phone || null,
          CustomerEMail: customerDetails.email
        },
        // Browser fingerprint fields at top level
        BrowserJavascriptEnabled: "1", // JavaScript is obviously enabled
        BrowserJavaEnabled: browserFingerprint.javaEnabled ? "1" : "0",
        BrowserColorDepth: browserFingerprint.colorDepth,
        BrowserScreenHeight: window.screen.height,
        BrowserScreenWidth: window.screen.width,
        BrowserTZ: browserFingerprint.timezone,
        BrowserLanguage: browserFingerprint.language
      };

      // Process payment
      const response = await spaService.processPayment(paymentRequest);

      if (!response.success) {
        throw new Error(response.error || 'Payment processing failed');
      }

      // Check payment status
      const status = response.paymentResult?.Status;

      if (status === 'OK') {
        // Payment successful!
        handlePaymentSuccess(response);
      } else if (status === '3DAUTH') {
        // 3D Secure authentication required
        setCurrentTransactionId(response.transactionId);
        setPaymentResult(response.paymentResult);
        setPaymentStep('3dsecure');
      } else {
        // Payment failed
        throw new Error(response.paymentResult?.StatusDetail || 'Payment was declined');
      }

    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Payment processing failed');
      setPaymentStep('payment');
      toast.current?.show({
        severity: 'error',
        summary: 'Payment Failed',
        detail: err.message || 'Payment processing failed',
        life: 5000
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Step 3: Handle 3D Secure completion (called from postMessage)
  const handle3DSecureComplete = async () => {
    try {
      setIsProcessing(false);
      setPaymentStep('processing');

      // The backend has already processed the 3DS and finalized the order
      // Show success message
      toast.current?.show({
        severity: 'success',
        summary: 'Payment Successful',
        detail: 'Your order has been completed successfully!',
        life: 5000
      });

      // Redirect to success page after a short delay
      setTimeout(() => {
        onHide();
        // Call success callback if provided
        if (onPaymentSuccess) {
          onPaymentSuccess();
        }
      }, 2000);

    } catch (err) {
      console.error('3DS completion error:', err);
      setError(err.message || '3D Secure authentication failed');
      setPaymentStep('payment');
      toast.current?.show({
        severity: 'error',
        summary: 'Authentication Failed',
        detail: err.message,
        life: 5000
      });
    }
  };

  // Handle successful payment
  const handlePaymentSuccess = (response) => {
    toast.current?.show({
      severity: 'success',
      summary: 'Payment Successful',
      detail: 'Your order has been completed successfully!',
      life: 5000
    });

    // Redirect to success URL after a short delay
    setTimeout(() => {
      if (response.redirectUrl) {
        window.location.href = response.redirectUrl;
      } else {
        // Call success callback if provided
        if (onPaymentSuccess) {
          onPaymentSuccess();
        }
        // Fallback: close dialog and refresh
        handleDialogHide();
      }
    }, 2000);
  };

  // Handle dialog close/reset
  const handleDialogHide = () => {
    setPaymentStep('details');
    setPaymentResult(null);
    setCurrentOrderId(null);
    setError(null);
    setIsProcessing(false);
    onHide();
  };

  // Calculate total
  const total = calculateTotal();

  // Render payment step content
  const renderStepContent = () => {
    if (paymentStep === 'payment') {
      return (
        <div className="payment-step">
          <h3 className="mt-0 mb-3">Enter Payment Details</h3>
          <PaymentForm
            onSubmit={handlePaymentSubmit}
            onCancel={() => setPaymentStep('details')}
            isLoading={isProcessing}
            total={total}
          />
        </div>
      );
    }

    if (paymentStep === 'processing') {
      return (
        <div className="text-center p-5">
          <i className="pi pi-spin pi-spinner" style={{ fontSize: '3rem' }}></i>
          <h3 className="mt-3">Processing Payment...</h3>
          <p className="text-600">Please wait while we process your payment securely.</p>
        </div>
      );
    }

    // Default: show details step
    return renderDetailsStep();
  };

  // Render the details collection step
  const renderDetailsStep = () => (
    <div className="grid">
      {error && (
        <div className="col-12">
          <Message severity="error" text={error} className="w-full mb-3" />
        </div>
      )}
        {/* Order Summary */}
        <div className="col-12">
          <Card>
            <h3 className="mt-0 mb-3">Order Summary</h3>
            
            {/* Item Name and Base Price */}
            <div className="flex justify-content-between align-items-center mb-2">
              <span>{item?.name || 'Item'}</span>
              <span className="font-bold">
                {itemType === 'event' && getDiscount() > 0 ? 
                  `£${getBasePrice().toFixed(2)}` : 
                  `£${getBasePrice().toFixed(2)}`
                }
              </span>
            </div>

            {/* Event Class Selection */}
            {itemType === 'event' && getSelectedClassName() && (
              <div className="flex justify-content-between align-items-center mb-2 text-sm">
                <span className="text-600">Class: {getSelectedClassName()}</span>
                <span className="text-600"></span>
              </div>
            )}

            {/* Event Horse Selection */}
            {itemType === 'event' && item?.selectedHorse && (
              <div className="flex justify-content-between align-items-center mb-2 text-sm">
                <span className="text-600">Horse: {item.selectedHorse}</span>
                <span className="text-600"></span>
              </div>
            )}

            {/* Product Quantity (if applicable) */}
            {itemType === 'product' && item?.quantity && item.quantity > 1 && (
              <div className="flex justify-content-between align-items-center mb-2 text-sm">
                <span className="text-600">Quantity: {item.quantity}</span>
                <span className="text-600">£{(getBasePrice() / item.quantity).toFixed(2)} each</span>
              </div>
            )}

            {/* Early Bird Discount for Events */}
            {itemType === 'event' && getDiscount() > 0 && (
              <div className="flex justify-content-between align-items-center mb-2 text-sm">
                <span className="text-600">Early Bird Discount</span>
                <span className="text-600">-£{getDiscount().toFixed(2)}</span>
              </div>
            )}

            {/* Product Options (if applicable) */}
            {itemType === 'product' && (item?.selectedSize || item?.selectedColor) && (
              <div className="mb-2 text-sm text-600">
                {item.selectedSize && <div>Size: {item.selectedSize}</div>}
                {item.selectedColor && <div>Color: {item.selectedColor}</div>}
              </div>
            )}

            {/* Horse Details (if applicable) */}
            {itemType === 'horse-registration' && item?.horseDetails?.horseName && (
              <div className="mb-2 text-sm text-600">
                <div>Horse: {item.horseDetails.horseName}</div>
                {item.horseDetails.gender && <div>Gender: {item.horseDetails.gender}</div>}
              </div>
            )}

            <Divider />
            <div className="flex justify-content-between align-items-center">
              <span className="font-bold text-lg">Total</span>
              <span className="font-bold text-lg se-text-primary">£{total.toFixed(2)}</span>
            </div>
          </Card>
        </div>

        {/* Customer Details */}
        <div className="col-12">
          <Card>
            <h3 className="mt-0 mb-3">Customer Details</h3>
            <div className="grid">
              <div className="col-6">
                <div className="se-form-group">
                  <label className="se-form-label">First Name *</label>
                  <InputText
                    value={customerDetails.firstName}
                    onChange={(e) => handleCustomerChange('firstName', e.target.value)}
                    onBlur={() => handleBlur('firstName')}
                    className={`w-full ${errors.firstName && touched.firstName ? 'p-invalid' : ''}`}
                  />
                  {errors.firstName && touched.firstName && (
                    <small className="p-error">{errors.firstName}</small>
                  )}
                </div>
              </div>
              <div className="col-6">
                <div className="se-form-group">
                  <label className="se-form-label">Last Name *</label>
                  <InputText
                    value={customerDetails.lastName}
                    onChange={(e) => handleCustomerChange('lastName', e.target.value)}
                    onBlur={() => handleBlur('lastName')}
                    className={`w-full ${errors.lastName && touched.lastName ? 'p-invalid' : ''}`}
                  />
                  {errors.lastName && touched.lastName && (
                    <small className="p-error">{errors.lastName}</small>
                  )}
                </div>
              </div>
              <div className="col-12">
                <div className="se-form-group">
                  <label className="se-form-label">Email *</label>
                  <InputText
                    value={customerDetails.email}
                    onChange={(e) => handleCustomerChange('email', e.target.value)}
                    onBlur={() => handleBlur('email')}
                    className={`w-full ${errors.email && touched.email ? 'p-invalid' : ''}`}
                    type="email"
                  />
                  {errors.email && touched.email && (
                    <small className="p-error">{errors.email}</small>
                  )}
                </div>
              </div>
              <div className="col-12">
                <div className="se-form-group">
                  <label className="se-form-label">Phone</label>
                  <InputText
                    value={customerDetails.phone}
                    onChange={(e) => handleCustomerChange('phone', e.target.value)}
                    onBlur={() => handleBlur('phone')}
                    className={`w-full ${errors.phone && touched.phone ? 'p-invalid' : ''}`}
                  />
                  {errors.phone && touched.phone && (
                    <small className="p-error">{errors.phone}</small>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Billing Address */}
        <div className="col-12">
          <Card>
            <h3 className="mt-0 mb-3">Billing Address</h3>
            <div className="grid">
              <div className="col-12">
                <div className="se-form-group">
                  <label className="se-form-label">Address Line 1 *</label>
                  <InputText
                    value={billingAddress.addressLine1}
                    onChange={(e) => handleAddressChange('addressLine1', e.target.value)}
                    onBlur={() => handleBlur('addressLine1')}
                    className={`w-full ${errors.addressLine1 && touched.addressLine1 ? 'p-invalid' : ''}`}
                  />
                  {errors.addressLine1 && touched.addressLine1 && (
                    <small className="p-error">{errors.addressLine1}</small>
                  )}
                </div>
              </div>
              <div className="col-12">
                <div className="se-form-group">
                  <label className="se-form-label">Address Line 2</label>
                  <InputText
                    value={billingAddress.addressLine2}
                    onChange={(e) => handleAddressChange('addressLine2', e.target.value)}
                    className="w-full"
                  />
                </div>
              </div>
              <div className="col-6">
                <div className="se-form-group">
                  <label className="se-form-label">City *</label>
                  <InputText
                    value={billingAddress.townCity}
                    onChange={(e) => handleAddressChange('townCity', e.target.value)}
                    onBlur={() => handleBlur('townCity')}
                    className={`w-full ${errors.townCity && touched.townCity ? 'p-invalid' : ''}`}
                  />
                  {errors.townCity && touched.townCity && (
                    <small className="p-error">{errors.townCity}</small>
                  )}
                </div>
              </div>
              <div className="col-6">
                <div className="se-form-group">
                  <label className="se-form-label">County</label>
                  <InputText
                    value={billingAddress.county}
                    onChange={(e) => handleAddressChange('county', e.target.value)}
                    className="w-full"
                  />
                </div>
              </div>
              <div className="col-6">
                <div className="se-form-group">
                  <label className="se-form-label">Postcode *</label>
                  <InputText
                    value={billingAddress.postCode}
                    onChange={(e) => handleAddressChange('postCode', e.target.value)}
                    onBlur={() => handleBlur('postCode')}
                    className={`w-full ${errors.postCode && touched.postCode ? 'p-invalid' : ''}`}
                  />
                  {errors.postCode && touched.postCode && (
                    <small className="p-error">{errors.postCode}</small>
                  )}
                </div>
              </div>
              <div className="col-6">
                <div className="se-form-group">
                  <label className="se-form-label">Country *</label>
                  <Dropdown
                    value={billingAddress.country}
                    options={countries}
                    onChange={(e) => handleAddressChange('country', e.value)}
                    onBlur={() => handleBlur('country')}
                    placeholder="Select Country"
                    className={`w-full ${errors.country && touched.country ? 'p-invalid' : ''}`}
                  />
                  {errors.country && touched.country && (
                    <small className="p-error">{errors.country}</small>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Action Buttons */}
        <div className="col-12">
          <Card>
            <div className="flex justify-content-center gap-2">
              <Button
                label="Cancel"
                icon="pi pi-times"
                className="p-button-text"
                onClick={handleDialogHide}
                disabled={isProcessing}
              />
              <Button
                label={`Proceed to Payment £${total.toFixed(2)}`}
                icon="pi pi-arrow-right"
                className="p-button-primary"
                onClick={handleProceedToPayment}
                loading={isProcessing}
                disabled={isProcessing}
              />
            </div>
          </Card>
        </div>
      </div>
    );

  return (
    <>
      <Toast ref={toast} />
      
      <Dialog
        header={paymentStep === 'payment' ? 'Payment Details' : 'Checkout'}
        visible={visible}
        style={{ width: '90vw', maxWidth: '800px' }}
        onHide={handleDialogHide}
        maximizable
        modal
        className="checkout-dialog"
        closable={!isProcessing}
        closeOnEscape={!isProcessing}
      >
        {renderStepContent()}
      </Dialog>

      {/* 3D Secure Modal */}
      <ThreeDSecureModal
        visible={paymentStep === '3dsecure'}
        onHide={() => {
          setPaymentStep('payment');
          setPaymentResult(null);
        }}
        onComplete={handle3DSecureComplete}
        paymentResult={paymentResult}
        orderId={currentOrderId}
        transactionId={currentTransactionId}
      />
    </>
  );
};

export default CheckoutDialog;